import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  CssBaseline,
  useTheme,
  Button,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Inventory,
  ShoppingCart,
  Analytics,
  AccountCircle,
  Logout,
  Article,
  ChevronLeft,
  ChevronRight,
  DarkMode,
  LightMode,
  Store,
  Undo,
  Handshake,
  Settings as SettingsIcon,
  AttachMoney,
  PersonAdd,
  AccountBalance,
  Storefront,
  Search,
  Favorite,
  RequestPage,
  Person,
  Campaign,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageSwitcher from '../LanguageSwitcher';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const logo = require('../logo.png');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const logoBlack = require('../logo_black.png');

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const menuItemDefs = [
  { key: 'dashboard', icon: <Dashboard />, path: '/seller' },
  { key: 'analytics', icon: <Analytics />, path: '/seller/analytics' },
  { key: 'orders', icon: <ShoppingCart />, path: '/seller/orders' },
  { key: 'shop', icon: <Store />, path: '/seller/shop' },
  { key: 'products', icon: <Inventory />, path: '/seller/products' },
  { key: 'contents', icon: <Article />, path: '/seller/contents' },
  { key: 'refunds', icon: <Undo />, path: '/seller/refunds' },
  { key: 'settlement', icon: <AccountBalance />, path: '/seller/settlement' },
  { key: 'profile', icon: <Person />, path: '/seller/profile' },
  { key: 'share2dm', icon: <Campaign />, path: '/seller/share2dm' },
  { key: 'divider', icon: null, path: '' },
  { key: 'productSettings', icon: <SettingsIcon />, path: '/seller/product-affiliate-settings' },
  { key: 'marketplace', icon: <Storefront />, path: '/seller/affiliate-marketplace' },
  { key: 'myWishlist', icon: <Favorite />, path: '/seller/affiliate-wishlist' },
  { key: 'myRequests', icon: <RequestPage />, path: '/seller/affiliate-requests' },
  { key: 'affiliatePartners', icon: <PersonAdd />, path: '/seller/affiliate-partners' },
  { key: 'brandPartners', icon: <Handshake />, path: '/seller/my-brand-partners' },
  { key: 'myEarnings', icon: <AttachMoney />, path: '/seller/my-affiliate-earnings' },
];


function Header({ onLogout, isDarkMode }: { onLogout: () => void; isDarkMode: boolean }) {
  const { t } = useTranslation();
  const [nickname, setNickname] = useState<string>('Seller');
  const [loading, setLoading] = useState(true);

  // 사용자 스토어명 가져오기
  useEffect(() => {
    let fetchDone = false;

    const fetchUserProfile = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 300));

        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          const { data: sellerProfile } = await supabase
            .from('seller_profile')
            .select('store_name')
            .eq('user_id', session.user.id)
            .single();

          setNickname(sellerProfile?.store_name || 'Seller');
        } else {
          setNickname('Seller');
        }
      } catch (error) {
        setNickname('Seller');
      } finally {
        fetchDone = true;
        setLoading(false);
      }
    };

    fetchUserProfile();

    const fallbackTimer = setTimeout(() => {
      if (fetchDone) return;
      setNickname('Seller');
      setLoading(false);
    }, 3000);

    return () => clearTimeout(fallbackTimer);
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
        borderRadius: 3,
        px: 3,
        py: 1.2,
        minHeight: 68,
        boxSizing: 'border-box',
        boxShadow: isDarkMode ? '0px 2px 4px rgba(0, 0, 0, 0.3)' : '0px 2px 4px rgba(0, 0, 0, 0.05)',
        border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        mb: 2,
      }}
    >
      {/* Logo Image */}
      <Box>
        <img
          src={isDarkMode ? logoBlack : logo}
          alt="Clozet Logo"
          style={{
            height: 48,
            width: 'auto',
            objectFit: 'contain',
            marginLeft: 2,
            transition: 'opacity 0.3s ease'
          }}
        />
      </Box>
      {/* Profile */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ textAlign: 'right', mr: 1.2 }}>
          <Typography fontWeight={600} fontSize={15} color={isDarkMode ? '#fff' : '#000'}>
            {loading ? t('common.loading') : nickname}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
            <LanguageSwitcher isDarkMode={isDarkMode} />
            <Button
              variant="text"
              size="small"
              sx={{
                color: isDarkMode ? '#ccc' : '#666',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: 13,
                minWidth: 0,
                p: 0
              }}
              onClick={onLogout}
            >
              {t('common.logout')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function SellerLayout() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [ordersCount, setOrdersCount] = useState<number>(0);
  const [refundsCount, setRefundsCount] = useState<number>(0);
  const [sellerStatus, setSellerStatus] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleDarkModeToggle = () => {
    setIsDarkMode(!isDarkMode);
    // localStorage에 다크모드 설정 저장
    localStorage.setItem('darkMode', (!isDarkMode).toString());
  };

  // 컴포넌트 마운트 시 다크모드 설정 불러오기
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode) {
      setIsDarkMode(savedDarkMode === 'true');
    }
  }, []);

  // 다크모드 변경 시 document에 테마 속성 설정
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }, [isDarkMode]);

  // seller status 체크 (pending이면 승인 대기 페이지로 리다이렉트)
  useEffect(() => {
    let cancelled = false;

    const checkSellerStatus = async () => {
      try {
        // AuthProvider의 getSession()과 동시 호출을 피하기 위해 약간 대기
        await new Promise(resolve => setTimeout(resolve, 500));
        if (cancelled) return;

        let { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          // 세션이 없으면 잠시 더 대기 후 재확인 (토큰 갱신 중일 수 있음)
          await new Promise(resolve => setTimeout(resolve, 1500));
          if (cancelled) return;

          const { data: { session: retrySession } } = await supabase.auth.getSession();
          if (!retrySession?.user) {
            navigate('/login');
            return;
          }
          // 복구된 세션으로 계속 진행 (session 변수 재할당)
          session = retrySession;
        }

        // 재시도 로직 (세션 확립 타이밍 이슈 대응)
        let sellerProfile = null;
        const maxRetries = 3;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          if (cancelled) return;

          if (attempt > 0) {
            console.log(`seller_profile 조회 재시도 ${attempt + 1}/${maxRetries}...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }

          const { data, error } = await supabase
            .from('seller_profile')
            .select('status')
            .eq('user_id', session.user.id)
            .single();

          if (!error && data) {
            sellerProfile = data;
            break;
          }

          console.warn(`seller_profile 조회 시도 ${attempt + 1} 실패:`, error);
        }

        if (cancelled) return;

        if (sellerProfile) {
          setSellerStatus(sellerProfile.status);

          // pending, rejected, suspended 상태면 승인 대기 페이지로 리다이렉트
          if (sellerProfile.status !== 'active') {
            navigate('/seller-pending');
          }
        }

      } catch (error) {
        console.error('Error checking seller status:', error);
      }
    };

    checkSellerStatus();

    return () => { cancelled = true; };
  }, [navigate]);

  // 초기 로드 시 카운트 조회 및 실시간 구독 설정
  useEffect(() => {
    // seller가 active 상태일 때만 카운트 로드
    if (sellerStatus !== 'active') return;

    // 초기 카운트 로드
    loadOrdersCount();
    loadRefundsCount();

    // order_items 테이블 실시간 구독
    const ordersSubscription = supabase
      .channel('orders-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'order_items',
        },
        () => {
          loadOrdersCount();
        }
      )
      .subscribe();

    // orders 테이블 실시간 구독 (payment_status 변경 감지)
    const ordersPaymentSubscription = supabase
      .channel('orders-payment-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
        },
        () => {
          loadOrdersCount();
        }
      )
      .subscribe();

    // cancel_exchange_requests 테이블 실시간 구독
    const refundsSubscription = supabase
      .channel('refunds-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cancel_exchange_requests',
        },
        () => {
          loadRefundsCount();
        }
      )
      .subscribe();

    // cleanup
    return () => {
      ordersSubscription.unsubscribe();
      ordersPaymentSubscription.unsubscribe();
      refundsSubscription.unsubscribe();
    };
  }, [sellerStatus]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Orders 카운트 조회 함수
  const loadOrdersCount = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      // seller_profile에서 seller_profile_id 가져오기
      const { data: sellerProfile } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', session.user.id)
        .single();

      if (!sellerProfile) return;

      // preparing 상태이면서 paid인 주문 카운트
      const { count, error } = await supabase
        .from('order_items')
        .select('*, orders!inner(payment_status), products!inner(seller_id)', { count: 'exact', head: true })
        .eq('products.seller_id', sellerProfile.id)
        .eq('item_delivery_status', 'preparing')
        .eq('orders.payment_status', 'paid');

      if (!error && count !== null) {
        setOrdersCount(count);
      }
    } catch (error) {
      console.error('Orders count 조회 실패:', error);
    }
  };

  // Refunds 카운트 조회 함수
  const loadRefundsCount = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      // seller_profile에서 seller_profile_id 가져오기
      const { data: sellerProfile } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', session.user.id)
        .single();

      if (!sellerProfile) return;

      // pending 상태인 취소/교환/반품 요청 카운트
      const { count, error } = await supabase
        .from('cancel_exchange_requests')
        .select('*, order_items!inner(*, products!inner(seller_id))', { count: 'exact', head: true })
        .eq('order_items.products.seller_id', sellerProfile.id)
        .eq('status', 'pending');

      if (!error && count !== null) {
        setRefundsCount(count);
      }
    } catch (error) {
      console.error('Refunds count 조회 실패:', error);
    }
  };

  // 로그아웃 함수: 즉시 이동 방식
  const handleLogout = () => {
    console.log('=== Seller 로그아웃 버튼 클릭됨 ===');

    // 1. 즉시 로컬 세션 정리
    console.log('로컬 세션 즉시 정리 시작...');

    // localStorage 완전 정리
    const localStorageKeys = Object.keys(localStorage);
    localStorageKeys.forEach(key => {
      if (key.includes('supabase') || key.includes('auth') || key.includes('session')) {
        localStorage.removeItem(key);
        console.log('localStorage 삭제:', key);
      }
    });

    // sessionStorage 완전 정리
    const sessionStorageKeys = Object.keys(sessionStorage);
    sessionStorageKeys.forEach(key => {
      if (key.includes('supabase') || key.includes('auth') || key.includes('session')) {
        sessionStorage.removeItem(key);
        console.log('sessionStorage 삭제:', key);
      }
    });

    // 쿠키 정리
    document.cookie.split(";").forEach(function(c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    console.log('로컬 세션 정리 완료');

    // 2. 즉시 로그인 페이지로 이동
    console.log('즉시 로그인 페이지로 이동...');
    window.location.replace('/login');

    // 3. 백그라운드에서 Supabase 로그아웃 처리 (사용자 대기 없음)
    supabase.auth.signOut().then(() => {
      console.log('백그라운드 Supabase 로그아웃 완료');
    }).catch((error) => {
      console.warn('백그라운드 Supabase 로그아웃 실패:', error);
    });
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ minHeight: 20, p: 0, mb: 0 }} />
      {/* Menu 텍스트 추가 */}
      <Box sx={{ px: 2, py: 0.3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {!sidebarCollapsed && (
          <Typography variant="subtitle1" fontWeight={700} color={isDarkMode ? '#fff' : '#000'}>{t('common.menu')}</Typography>
        )}
        <IconButton
          onClick={handleSidebarToggle}
          sx={{
            color: isDarkMode ? '#ccc' : '#666',
            '&:hover': { color: isDarkMode ? '#fff' : '#000' }
          }}
        >
          {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <List sx={{ flex: 1 }}>
        {menuItemDefs.map((item, index) => {
          // 구분선 처리
          if (item.key === 'divider') {
            return (
              <Box key={`divider-${index}`} sx={{ my: 1 }}>
                <Divider />
                {!sidebarCollapsed && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2,
                      pt: 1.5,
                      pb: 0.5,
                      display: 'block',
                      color: isDarkMode ? '#9ca3af' : '#6b7280',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('nav.seller.affiliate')}
                  </Typography>
                )}
              </Box>
            );
          }

          // Badge 카운트 가져오기
          const getBadgeCount = () => {
            if (item.key === 'orders') return ordersCount;
            if (item.key === 'refunds') return refundsCount;
            return 0;
          };

          const badgeCount = getBadgeCount();
          const badgeContent = badgeCount > 99 ? '99+' : badgeCount;

          return (
            <ListItem key={item.key} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  bgcolor: location.pathname === item.path ? (isDarkMode ? '#404040' : '#f5f5f5') : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: isDarkMode ? '#404040' : '#f0f0f0',
                    color: isDarkMode ? '#fff' : '#000',
                    fontWeight: 700,
                  },
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                }}
              >
                <ListItemIcon sx={{
                  color: location.pathname === item.path ? (isDarkMode ? '#fff' : '#000') : (isDarkMode ? '#ccc' : '#666'),
                  minWidth: sidebarCollapsed ? 24 : 36
                }}>
                  {badgeCount > 0 ? (
                    <Badge
                      badgeContent={badgeContent}
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          height: '18px',
                          minWidth: '18px',
                          fontWeight: 700,
                        }
                      }}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <ListItemText
                    primary={t(`nav.seller.${item.key}`)}
                    sx={{
                      fontWeight: location.pathname === item.path ? 700 : 400,
                      '& .MuiListItemText-primary': {
                        color: isDarkMode ? '#fff' : '#000'
                      }
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* 다크모드 토글 버튼 */}
      <Box sx={{ p: 2, mt: 'auto' }}>
        <ListItemButton
          onClick={handleDarkModeToggle}
          sx={{
            borderRadius: 2,
            bgcolor: 'transparent',
            '&:hover': { bgcolor: isDarkMode ? '#404040' : '#f5f5f5' },
            minHeight: 48,
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          }}
        >
          <ListItemIcon sx={{
            color: isDarkMode ? '#ccc' : '#666',
            minWidth: sidebarCollapsed ? 24 : 36
          }}>
            {isDarkMode ? <LightMode /> : <DarkMode />}
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText
              primary={isDarkMode ? t('common.lightMode') : t('common.darkMode')}
              sx={{
                fontWeight: 400,
                '& .MuiListItemText-primary': {
                  color: isDarkMode ? '#fff' : '#000'
                }
              }}
            />
          )}
        </ListItemButton>
        {sidebarCollapsed && (
          <Box sx={{ mt: 1 }}>
            <LanguageSwitcher isDarkMode={isDarkMode} collapsed />
          </Box>
        )}
        {!sidebarCollapsed && (
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: isDarkMode ? '#555' : '#bbb', fontSize: '0.7rem' }}>
            v{process.env.REACT_APP_VERSION || require('../../../package.json').version}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      width: '100vw',
      minHeight: '100vh',
      bgcolor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
      display: 'flex',
      flexDirection: 'row',
      transition: 'background-color 0.3s ease'
    }}>
      <CssBaseline />
      {/* 상단 헤더 */}
      <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', zIndex: 1201 }}>
        <Header onLogout={handleLogout} isDarkMode={isDarkMode} />
      </Box>
      {/* 사이드바 */}
      <Box
        component="nav"
        sx={{
          width: { sm: sidebarCollapsed ? collapsedDrawerWidth + 16 : drawerWidth + 16 },
          flexShrink: { sm: 0 },
          p: 0,
          mt: '110px',
          transition: 'width 0.3s ease-in-out'
        }}
        aria-label="sidebar"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
              border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
              borderRadius: 4,
              boxShadow: isDarkMode ? '0px 2px 4px rgba(0, 0, 0, 0.3)' : '0px 2px 4px rgba(0, 0, 0, 0.05)',
              top: '110px',
              height: 'calc(100vh - 110px)',
              m: 0,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
              border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
              borderRadius: 4,
              boxShadow: isDarkMode ? '0px 2px 4px rgba(0, 0, 0, 0.3)' : '0px 2px 4px rgba(0, 0, 0, 0.05)',
              top: '110px',
              height: 'calc(100vh - 110px)',
              m: 0,
              transition: 'width 0.3s ease-in-out, background-color 0.3s ease, border-color 0.3s ease',
            },
          }}
          open={true}
        >
          {drawer}
        </Drawer>
      </Box>
      {/* 메인 컨텐츠 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 4 },
          width: { sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth + 16 : drawerWidth + 16}px)` },
          mt: { xs: 16, sm: 16 },
          bgcolor: isDarkMode ? '#1a1a1a' : 'transparent',
          transition: 'width 0.3s ease-in-out, background-color 0.3s ease',
          minHeight: '100vh',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
